/**
 * Success Page Component - Shows results after payment
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { calculatorAPI } from '../services/api';
import { ResultsResponse } from '../types';

const Success: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState<ResultsResponse['results'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  const pollForResults = useCallback((clientSessionId: string) => {
    const maxAttempts = 30; // 最多等待5分钟 (30 * 10秒)
    const interval = setInterval(async () => {
      try {
        setPollAttempts(prev => prev + 1);
        const response = await calculatorAPI.getResults(clientSessionId);

        if (response.success && response.results) {
          clearInterval(interval);
          setResults(response.results);
          setLoading(false);
          console.log('Results received:', response.results);
        }
      } catch (error: any) {
        console.log('Poll attempt failed:', error?.response?.status);

        if (pollAttempts >= maxAttempts) {
          clearInterval(interval);
          setError('Results processing timed out. Please check your email for the report, or contact support.');
          setLoading(false);
        }
      }
    }, 10000); // 每10秒检查一次

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [pollAttempts]);

  useEffect(() => {
    const session = searchParams.get('session_id');
    const clientSession = searchParams.get('client_session');

    console.log('Payment completed for session:', session);
    console.log('Client session:', clientSession);

    if (clientSession) {
      pollForResults(clientSession);
    } else {
      setError('Session information not found');
      setLoading(false);
    }
  }, [searchParams, pollForResults]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Icon */}
          {!loading && !error && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Payment Successful!
              </h1>
              <p className="text-gray-600">
                Your property division report is ready
              </p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-xl p-8 text-center mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Processing Your Report</h2>
              <p className="text-gray-600 mb-4">
                We're analyzing your information and generating your personalized report.
              </p>
              <p className="text-sm text-gray-500">
                This usually takes 30-60 seconds...
              </p>
              {pollAttempts > 0 && (
                <p className="text-xs text-gray-400 mt-2">
                  Checking... ({pollAttempts}/30)
                </p>
              )}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-white rounded-lg shadow-xl p-8 text-center mb-6 border-2 border-red-200">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-blue-700 transition duration-300"
              >
                Start Over
              </button>
            </div>
          )}

          {/* Results Display */}
          {!loading && !error && results && (
            <>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Property Division Results</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">Net Assets</div>
                    <div className="text-2xl font-bold text-blue-600">
                      ${results.netAssets.toLocaleString()}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">Your Share</div>
                    <div className="text-2xl font-bold text-green-600">
                      {results.ratio.toFixed(1)}% ({results.userShare.toFixed(2)}%)
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                    <div>
                      <div className="font-semibold text-gray-900">Your Share</div>
                      <div className="text-sm text-gray-600">
                        {results.ratio.toFixed(1)}% of net assets
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        ${results.userShare.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-semibold text-gray-900">Spouse Share</div>
                      <div className="text-sm text-gray-600">
                        {(100 - results.ratio).toFixed(1)}% of net assets
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-600">
                        ${results.spouseShare.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-2 gap-4">
                  <div className="text-sm">
                    <span className="text-gray-600">Total Assets:</span>{' '}
                    <span className="font-semibold">${results.totalAssets.toLocaleString()}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Total Liabilities:</span>{' '}
                    <span className="font-semibold">${results.totalLiabilities.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Email Notice */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📧</span>
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-1">Detailed Report Sent!</h3>
                    <p className="text-sm text-blue-700">
                      A complete PDF report with detailed calculations, state law references,
                      and legal disclaimers has been sent to your email address.
                      Please check your inbox (and spam folder) if you don't see it within a few minutes.
                    </p>
                  </div>
                </div>
              </div>

              {/* Return Home Button */}
              <button
                onClick={() => navigate('/')}
                className="w-full bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-700 transition duration-300"
              >
                Return to Home
              </button>
            </>
          )}

          {/* Support */}
          <div className="text-center text-sm text-gray-600 mt-6">
            <p>
              Don't see the email after 15 minutes?{' '}
              <button className="text-blue-600 hover:underline">
                Contact support
              </button>
            </p>
          </div>

          {/* Legal Note */}
          {!loading && (
            <div className="mt-6 text-xs text-gray-500 text-center bg-white/50 p-4 rounded-lg">
              <p>
                <strong>Important:</strong> This report is for informational purposes only and does not
                constitute legal advice. Consult with a qualified attorney before making decisions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Success;
